import useAccount from "@/modules/account/hooks/useAccount";
import * as auth from "@/services/auth";
import { useCallback, useState } from "react";
import StepProvideEmail from "./components/StepProvideEmail";
import StepVerification from "./components/StepVerification";
import StepAuthorizedSuccess from "./components/StepAuthorizedSuccess";
import StepUnauthorizedSuccess from "./components/StepUnauthorizedSuccess";
import { FormDataStep1, FormDataStep2 } from "./types";

enum Step {
  initial,
  confirm,
  success,
}

interface ResetPasswordProps {
  showSignInLink?: boolean;
}

const ResetPassword = ({ showSignInLink }: ResetPasswordProps) => {
  const [step, setStep] = useState(Step.initial);
  const [resetErrorCode, setResetErrorCode] = useState<string>();
  const { account, isSignedIn } = useAccount();
  const [email, setEmail] = useState<string>(account?.email ?? "");
  const [verificationCodeSentTo, setVerificationCodeSentTo] = useState<
    string | undefined
  >();

  const resetPassword = useCallback(async ({ email }: FormDataStep1) => {
    const details = await auth.forgotPassword(email);
    setStep(Step.confirm);
    setEmail(email);
    setVerificationCodeSentTo(details.Destination);
  }, []);

  const onSendAgain = useCallback(() => {
    setStep(Step.initial);
    setVerificationCodeSentTo(undefined);
  }, []);

  const confirmPassword = useCallback(
    async ({ code, password }: FormDataStep2) => {
      try {
        await auth.confirmPassword(email, code, password);
        setStep(Step.success);
        setVerificationCodeSentTo(undefined);
      } catch (error) {
        setResetErrorCode(error.code);
      }
    },
    [email],
  );

  return (
    <>
      {step === Step.initial && (
        <StepProvideEmail
          onSubmit={resetPassword}
          showSignInLink={showSignInLink}
          email={email}
        />
      )}
      {step === Step.confirm && (
        <StepVerification
          onSubmit={confirmPassword}
          email={email}
          sentTo={verificationCodeSentTo}
          errorCode={resetErrorCode}
          onSendAgain={onSendAgain}
        />
      )}
      {step === Step.success && !isSignedIn() && <StepUnauthorizedSuccess />}
      {step === Step.success && isSignedIn() && <StepAuthorizedSuccess />}
    </>
  );
};

export default ResetPassword;
